import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/auth.service';
import { 
    Box, 
    TextField, 
    Button, 
    Typography, 
    Paper, 
    Alert,
    InputAdornment,
    IconButton,
    Fade,
    Slide
} from '@mui/material';
import { 
    Visibility, 
    VisibilityOff, 
    Email as EmailIcon,
    Lock as LockIcon
} from '@mui/icons-material';
import styled from '@emotion/styled';

// Styled components for modern design
const LoginContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        opacity: 0.3,
    }
}));

const LoginCard = styled(Paper)(({ theme }) => ({
    padding: '48px 40px',
    borderRadius: '24px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    maxWidth: '420px',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    }
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '32px',
    '& img': {
        width: '80px',
        height: '80px',
        borderRadius: '16px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        marginBottom: '16px',
    }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: '24px',
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#ffffff',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: '#f8f9fa',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#667eea',
                borderWidth: '2px',
            },
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#667eea',
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root': {
        color: '#6c757d',
        '&.Mui-focused': {
            color: '#667eea',
        },
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '12px',
    padding: '14px 32px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '16px',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
        transform: 'translateY(-2px)',
        boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
    },
    '&:active': {
        transform: 'translateY(0)',
    },
}));

const AppName = styled(Typography)(({ theme }) => ({
    fontSize: '28px',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '8px',
}));

const AppTagline = styled(Typography)(({ theme }) => ({
    color: '#6c757d',
    fontSize: '16px',
    fontWeight: 400,
}));

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!username.trim()) {
            setError('Username is required');
            return;
        }
        if (!password.trim()) {
            setError('Password is required');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            await login({ username: username.trim(), password: password.trim() });
            navigate('/dashboard/chat');
        } catch (error) {
            setError("Invalid username or password");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordToggle = () => {
        setShowPassword(!showPassword);
    };

    const appName = process.env.REACT_APP_PHONE_NAME || 'WAPR';
    const appLogo = process.env.REACT_APP_APP_LOGO || "https://firebasestorage.googleapis.com/v0/b/segenesis-cotizador.appspot.com/o/notificaciones%2FGenesisLOGO%20vida%20y%20salud%20(1).png?alt=media&token=2e7ce64b-a316-4579-b3de-37468e2fa470";

    return (
        <LoginContainer>
            <Slide direction="up" in={true} timeout={600}>
                <LoginCard elevation={8}>
                    <LogoContainer>
                        <img 
                            src={appLogo} 
                            alt={`${appName} Logo`}
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                        <AppName variant="h4">
                            {appName}
                        </AppName>
                        <AppTagline variant="body1">
                            Welcome back! Please sign in to continue.
                        </AppTagline>
                    </LogoContainer>

                    <Fade in={true} timeout={800}>
                        <Box component="form" onSubmit={handleSubmit}>
                            <StyledTextField
                                fullWidth
                                label="Username"
                                variant="outlined"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailIcon sx={{ color: '#6c757d' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                disabled={isLoading}
                            />

                            <StyledTextField
                                fullWidth
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                variant="outlined"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon sx={{ color: '#6c757d' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handlePasswordToggle}
                                                edge="end"
                                                disabled={isLoading}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                disabled={isLoading}
                            />

                            {error && (
                                <Fade in={true} timeout={300}>
                                    <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                                        {error}
                                    </Alert>
                                </Fade>
                            )}

                            <StyledButton
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                sx={{ mt: 2 }}
                            >
                                {isLoading ? 'Signing in...' : 'Sign In'}
                            </StyledButton>
                        </Box>
                    </Fade>
                </LoginCard>
            </Slide>
        </LoginContainer>
    );
}

